// Single source of truth for which shopping actions a product allows. Used by
// ProductCard, ProductDetailPage, Cart/Wishlist/Compare pages, and the home /
// listing sections so the rules never drift between screens.
//
// Accepts both API shape (snake_case: enable_cart, price_visibility, ...) and
// stored/localStorage shape (camelCase: enableCart, priceVisibility, ...).

const pick = (product, camel, snake) =>
  product?.[camel] !== undefined ? product[camel] : product?.[snake];

const fields = (product) => ({
  enableCart: !!pick(product, 'enableCart', 'enable_cart'),
  enableInquiry: !!pick(product, 'enableInquiry', 'enable_inquiry'),
  priceVisibility: pick(product, 'priceVisibility', 'price_visibility'),
  stockStatus: pick(product, 'stockStatus', 'stock_status'),
  allowWishlist: !!pick(product, 'allowWishlist', 'allow_wishlist'),
  allowCompare: !!pick(product, 'allowCompare', 'allow_compare'),
});

const BLOCKED_STOCK = ['out_of_stock', 'discontinued'];
const QUOTE_STOCK = ['out_of_stock', 'special_order'];
const QUOTE_VISIBILITY = ['contact_for_price', 'inquiry_only'];

export function canAddToCart(product) {
  const f = fields(product);
  return (
    f.enableCart &&
    f.priceVisibility === 'show_price' &&
    !BLOCKED_STOCK.includes(f.stockStatus)
  );
}

export function canRequestQuote(product) {
  const f = fields(product);
  if (!f.enableInquiry) return false;
  return QUOTE_VISIBILITY.includes(f.priceVisibility) || QUOTE_STOCK.includes(f.stockStatus);
}

export function canWishlist(product) {
  return fields(product).allowWishlist;
}

export function canCompare(product) {
  return fields(product).allowCompare;
}

// The single most relevant CTA for a product, used when space is tight.
// Returns one of: add_to_cart | request_quote | contact_for_price |
// login_to_view_price | view.
export function getPrimaryProductAction(product) {
  if (canAddToCart(product)) return 'add_to_cart';
  const f = fields(product);
  if (f.priceVisibility === 'login_to_view_price') return 'login_to_view_price';
  if (canRequestQuote(product)) return 'request_quote';
  if (f.priceVisibility === 'contact_for_price') return 'contact_for_price';
  return 'view';
}

export const PRIMARY_ACTION_LABELS = {
  add_to_cart: 'Add to Cart',
  request_quote: 'Request Quote',
  contact_for_price: 'Contact for Price',
  login_to_view_price: 'Login to View Price',
  view: 'View Product',
};
